import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Button,
  Card,
  Col,
  Form,
  Input,
  Row,
  Statistic,
  Switch,
  Table,
  Tag,
  Typography,
  message,
} from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { ReloadOutlined, SaveOutlined } from '@ant-design/icons';
import { API_ENDPOINTS } from '../../api/config';
import { get, put } from '../../api/request';
import './DataReportingPage.css';

const { Paragraph, Text } = Typography;

type PolarisConfig = {
  enabled: boolean;
  app_id: string;
  app_secret: string;
  public_url: string;
  source_version: string;
};

type PullRecord = {
  id: number;
  status: 'success' | 'failed';
  code: string;
  message: string;
  app_id: string;
  start_date: string;
  end_date: string;
  item_count: number;
  request_ip: string;
  created_at: string;
};

type Overview = {
  config: Omit<PolarisConfig, 'app_secret'>;
  registered_users: number;
  ai_calls: number;
  pulls: { success: number; failed: number };
  latest_record?: PullRecord;
};

type ListResponse<T> = { data: T[]; total: number };

const dateText = (value?: string) => (value ? new Date(value).toLocaleString() : '-');

const DataReportingPage: React.FC = () => {
  const [form] = Form.useForm<PolarisConfig>();
  const [overview, setOverview] = useState<Overview>();
  const [records, setRecords] = useState<PullRecord[]>([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [overviewData, recordData, configData] = await Promise.all([
        get<Overview>(API_ENDPOINTS.dataReporting.overview),
        get<ListResponse<PullRecord>>(API_ENDPOINTS.dataReporting.records),
        get<PolarisConfig>(API_ENDPOINTS.dataReporting.config),
      ]);
      setOverview(overviewData);
      setRecords(recordData.data || []);
      form.setFieldsValue(configData);
    } catch (error) {
      message.error(error instanceof Error ? error.message : '加载北极星数据上报信息失败');
    } finally {
      setLoading(false);
    }
  }, [form]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const saveConfig = async (values: PolarisConfig) => {
    setSaving(true);
    try {
      const saved = await put<PolarisConfig>(API_ENDPOINTS.dataReporting.config, values);
      form.setFieldsValue(saved);
      message.success('北极星数据上报配置已保存');
      await loadData();
    } catch (error) {
      message.error(error instanceof Error ? error.message : '保存配置失败');
    } finally {
      setSaving(false);
    }
  };

  const columns: ColumnsType<PullRecord> = useMemo(() => [
    { title: '记录', dataIndex: 'id', width: 90, render: (value) => `#${value}` },
    {
      title: '状态', dataIndex: 'status', width: 90,
      render: (value: PullRecord['status']) => (
        <Tag color={value === 'success' ? 'success' : 'error'}>{value === 'success' ? '成功' : '失败'}</Tag>
      ),
    },
    { title: '日期范围', width: 210, render: (_, row) => `${row.start_date || '-'} 至 ${row.end_date || '-'}` },
    { title: '返回天数', dataIndex: 'item_count', width: 100 },
    { title: '请求 IP', dataIndex: 'request_ip', width: 150, render: (value) => value || '-' },
    { title: '结果码', dataIndex: 'code', width: 140 },
    { title: '说明', dataIndex: 'message', ellipsis: true, render: (value) => value || '-' },
    { title: '拉取时间', dataIndex: 'created_at', width: 180, render: dateText },
  ], []);

  const endpoint = Form.useWatch('public_url', form) || overview?.config.public_url || '';

  return (
    <div className="data-reporting-page">
      <div className="reporting-header">
        <div>
          <h1>北极星数据上报</h1>
          <Text type="secondary">按北京时间业务日向北极星提供平台用户与 AI 流程使用数据</Text>
        </div>
        <Button icon={<ReloadOutlined />} loading={loading} onClick={loadData}>刷新</Button>
      </div>

      <Alert
        showIcon
        type="info"
        message="北极星主动拉取，无需手动上报"
        description="北极星约每 5 分钟请求一次同步接口。当前只提供用户和 AI 使用统计，不提供支付、订单、充值或金额数据。单次日期范围最多 31 天。"
      />

      <Row className="reporting-metrics" gutter={20}>
        <Col xs={12} lg={6}><Statistic title="累计注册用户" value={overview?.registered_users || 0} /></Col>
        <Col xs={12} lg={6}><Statistic title="累计 AI 调用" value={overview?.ai_calls || 0} /></Col>
        <Col xs={12} lg={6}><Statistic title="成功拉取" value={overview?.pulls.success || 0} /></Col>
        <Col xs={12} lg={6}>
          <Statistic title="失败拉取" value={overview?.pulls.failed || 0} valueStyle={overview?.pulls.failed ? { color: '#cf1322' } : undefined} />
        </Col>
      </Row>

      <Card
        className="reporting-config"
        title="北极星接口配置"
        extra={<Button type="primary" icon={<SaveOutlined />} loading={saving} onClick={() => form.submit()}>保存配置</Button>}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={saveConfig}
          initialValues={{
            enabled: true,
            app_id: 'AI-004',
            public_url: 'http://122.51.59.105:65183/api/v1/polaris/sync',
            source_version: 'v1',
          }}
        >
          <Row gutter={18}>
            <Col xs={24} md={4}>
              <Form.Item name="enabled" label="启用接口" valuePropName="checked">
                <Switch checkedChildren="启用" unCheckedChildren="停用" />
              </Form.Item>
            </Col>
            <Col xs={24} md={8}>
              <Form.Item name="app_id" label="App ID" rules={[{ required: true }]}><Input /></Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item name="source_version" label="统计口径版本" rules={[{ required: true }]}><Input /></Form.Item>
            </Col>
          </Row>
          <Form.Item name="app_secret" label="App Secret" rules={[{ required: true, message: '请输入北极星 App Secret' }]}>
            <Input.Password visibilityToggle autoComplete="new-password" />
          </Form.Item>
          <Form.Item name="public_url" label="北极星拉取地址" rules={[{ required: true, type: 'url' }]}>
            <Input />
          </Form.Item>
          <Paragraph type="secondary" className="secret-note">
            App Secret 仅用于请求头鉴权，保存于服务端配置中，不会进入上报内容或拉取记录。
          </Paragraph>
        </Form>
      </Card>

      <Card className="reporting-action" title="接口说明">
        <Paragraph copyable={{ text: endpoint }}><Text strong>GET </Text>{endpoint || '-'}</Paragraph>
        <Paragraph>
          请求头：<Text code>X-App-Id</Text>、<Text code>X-App-Secret</Text>；查询参数：
          <Text code>startDate=YYYY-MM-DD</Text>、<Text code>endDate=YYYY-MM-DD</Text>。
        </Paragraph>
        <Text type="secondary">成功响应固定返回 appType=app；经营数据 productOperations 为空数组。</Text>
      </Card>

      <Card className="reporting-table-card" title="北极星拉取记录">
        <Table rowKey="id" columns={columns} dataSource={records} loading={loading} pagination={{ pageSize: 12 }} scroll={{ x: 1160 }} />
      </Card>
    </div>
  );
};

export default DataReportingPage;
